import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "../modern-ui/sonner";
import { useScrollLock } from "../../hooks/useScrollLock";
import {
  ModalOverlay,
  ModalContainer,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ClaimSection,
  ClaimTitle,
  ClaimDescription,
  ClaimButton,
  TimerReveal,
  TimerContainer,
  TimerBadge,
  TimerDigit,
  TimerSeparator,
  TimerLabel,
  ClaimSuccessOverlay,
  FloatingText,
  ConfettiBurst,
  Divider,
  ReferralSection,
  ReferralHeader,
  ReferralIconWrap,
  ReferralInfo,
  ReferralTitle,
  ReferralDescription,
  ReferralCapNote,
  ReferralLinkContainer,
  ReferralLink,
  CopyButton,
  InviteButton,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  ProgressText,
} from "./FreeCreditsModal.styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAddDailyCreditsLoading,
  selectUser,
} from "../../store/slices/user/slice";
import { addDailyCredits } from "../../store/slices/user/asyncThunk";
import { MAX_USAGE_AFFILIATE } from "../../utils/constants";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as GiftSvg } from "../../assets/icons/gift.svg";
import { ReactComponent as UsersSvg } from "../../assets/icons/users.svg";
import { ReactComponent as CopySvg } from "../../assets/icons/copy.svg";
import { ReactComponent as SuccessSvg } from "../../assets/icons/success.svg";

const CLAIM_SOUND_URL =
  "https://cdn.pixabay.com/audio/2022/03/10/audio_c8c82764cb.mp3";

const FreeCreditsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 24,
    minutes: 0,
    seconds: 0,
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const user = useSelector(selectUser);
  const referralLink = user?.affiliateLink;
  const loading = useSelector(selectAddDailyCreditsLoading);

  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useScrollLock(shouldRender);

  const canClaim =
    !user?.lastFreeCredits ||
    (timeRemaining.hours === 0 &&
      timeRemaining.minutes === 0 &&
      timeRemaining.seconds === 0);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(false);
      const timer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      return () => cancelAnimationFrame(timer);
    } else if (shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setShowCelebration(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!user?.lastFreeCredits) {
      setTimeRemaining({ hours: 24, minutes: 0, seconds: 0 });
      return;
    }

    const calculateRemainingTime = () => {
      const now = new Date();
      const lastClaim = new Date(user?.lastFreeCredits);
      const nextClaim = new Date(lastClaim.getTime() + 24 * 60 * 60 * 1000);
      const timeDiff = nextClaim.getTime() - now.getTime();

      if (timeDiff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        hours: Math.floor(timeDiff / (1000 * 60 * 60)),
        minutes: Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((timeDiff % (1000 * 60)) / 1000),
      };
    };

    setTimeRemaining(calculateRemainingTime());

    const interval = setInterval(() => {
      const remaining = calculateRemainingTime();
      setTimeRemaining(remaining);

      if (
        remaining.hours === 0 &&
        remaining.minutes === 0 &&
        remaining.seconds === 0
      ) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, user?.lastFreeCredits]);

  const playCelebrationSound = useCallback(() => {
    try {
      const audio = new Audio(CLAIM_SOUND_URL);
      audio.volume = 0.4;
      audio.play().catch(() => {});
    } catch {
      // Sound not critical
    }
  }, []);

  if (!shouldRender) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error("Failed to copy link. Please try again.");
    }
  };

  const handleCollectCredits = () => {
    dispatch(addDailyCredits())
      .unwrap()
      .then(() => {
        setShowCelebration(true);
        playCelebrationSound();
        toast.success("Daily credits collected! +5 credits");
        setTimeout(() => setShowCelebration(false), 1500);
      })
      .catch((err) => {
        toast.error("Failed to collect credits. " + err.message);
      });
  };

  const handleShare = async () => {
    const shareData = {
      title: "Join Fapify - NSFW AI Video Generation",
      text: "Create amazing NSFW AI videos with Fapify! Use my referral link to get started.",
      url: referralLink,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success("Invite shared successfully!");
      } else {
        await navigator.clipboard.writeText(
          `${shareData.text} ${shareData.url}`,
        );
        toast.success("Invite message copied to clipboard!");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error sharing:", err);
        toast.error("Failed to share invite. Please try again.");
      }
    }
  };

  const formatTime = (time) => String(time).padStart(2, "0");

  if (!user) {
    return null;
  }

  return createPortal(
    <ModalOverlay onClick={handleOverlayClick} $isAnimating={isAnimating}>
      <ModalContainer $isAnimating={isAnimating}>
        <ModalContent>
          {/* Header */}
          <ModalHeader>
            <ModalTitle>Free Drops</ModalTitle>
            <CloseButton onClick={onClose}>
              <CloseSvg />
            </CloseButton>
          </ModalHeader>
          <ModalSubtitle>
            Claim daily drops or invite friends to earn more.
          </ModalSubtitle>

          {/* Daily Claim */}
          <ClaimSection>
            {showCelebration && (
              <ClaimSuccessOverlay>
                <ConfettiBurst />
                <FloatingText $x="30%" $y="40%" $delay="0s">
                  +5
                </FloatingText>
                <FloatingText $x="70%" $y="35%" $delay="0.1s">
                  +5
                </FloatingText>
                <FloatingText $x="50%" $y="25%" $delay="0.05s">
                  +5
                </FloatingText>
                <FloatingText $x="20%" $y="50%" $delay="0.15s">
                  +5
                </FloatingText>
                <FloatingText $x="80%" $y="45%" $delay="0.2s">
                  +5
                </FloatingText>
              </ClaimSuccessOverlay>
            )}

            {/* <ClaimIconWrap>
              <GiftSvg width={28} height={28} />
            </ClaimIconWrap> */}

            <ClaimTitle>
              <GiftSvg width={18} height={18} />
              Daily Reward
            </ClaimTitle>
            <ClaimDescription>
              {canClaim
                ? "Your free drops are ready! Tap to claim now."
                : "Come back when the timer hits zero."}
            </ClaimDescription>

            <ClaimButton
              onClick={handleCollectCredits}
              disabled={!canClaim || loading}
              $canClaim={canClaim}
            >
              {canClaim ? "Claim +5 Drops" : "Claimed"}
            </ClaimButton>

            <TimerReveal $visible={!canClaim} aria-hidden={canClaim}>
              <div>
                <TimerContainer>
                  <TimerBadge>
                    <TimerDigit>{formatTime(timeRemaining.hours)}</TimerDigit>
                    <TimerSeparator>:</TimerSeparator>
                    <TimerDigit>{formatTime(timeRemaining.minutes)}</TimerDigit>
                    <TimerSeparator>:</TimerSeparator>
                    <TimerDigit>{formatTime(timeRemaining.seconds)}</TimerDigit>
                  </TimerBadge>
                </TimerContainer>
                <TimerLabel>until next claim</TimerLabel>
              </div>
            </TimerReveal>
          </ClaimSection>

          <Divider />

          {/* Referral Section */}
          <ReferralSection>
            <ReferralHeader>
              <ReferralIconWrap>
                <UsersSvg width={20} height={20} />
              </ReferralIconWrap>
              <ReferralInfo>
                <ReferralTitle>Invite & Earn</ReferralTitle>
                <ReferralDescription>
                  Get 5 drops for each friend who signs up!
                </ReferralDescription>
              </ReferralInfo>
            </ReferralHeader>

            {user?.usageOfAffiliate >= MAX_USAGE_AFFILIATE && (
              <ReferralCapNote>
                First 400 referrals grant free drops. Affiliate earnings from
                purchases are uncapped.
              </ReferralCapNote>
            )}

            <ReferralLinkContainer>
              <ReferralLink>
                <span>{referralLink?.replace("https://www.", "")}</span>
              </ReferralLink>
              <CopyButton onClick={handleCopyLink} $success={copySuccess}>
                {copySuccess ? <SuccessSvg /> : <CopySvg />}
              </CopyButton>
            </ReferralLinkContainer>

            <InviteButton onClick={handleShare}>Invite Friends</InviteButton>

            <ProgressContainer>
              <ProgressBar>
                <ProgressFill
                  $progress={
                    (Math.min(user?.usageOfAffiliate ?? 0, 400) /
                      MAX_USAGE_AFFILIATE) *
                    100
                  }
                />
              </ProgressBar>
              <ProgressText>
                {user?.usageOfAffiliate ?? 0} friends invited
              </ProgressText>
            </ProgressContainer>
          </ReferralSection>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>,
    document.body,
  );
};

export default FreeCreditsModal;
